import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

/* Whether this login may open Statistics: super admins, and anyone who owns or has
   stats access on at least one account or co-manages an agency. Asked server-side
   (per-account permissions are fetched lazily), once per login: the sidebar that
   shows the nav item mounts on every page. A failed check is not remembered. */
let cached: { userId: string; access: Promise<boolean>; value?: boolean } | null = null;

function statsAccess(userId: string): Promise<boolean> {
  if (cached?.userId !== userId) {
    const entry: { userId: string; access: Promise<boolean>; value?: boolean } = {
      userId,
      access: api.getChatStatsAccess().then((res) => {
        entry.value = !!res.has_access;
        return entry.value;
      }),
    };
    entry.access.catch(() => {
      if (cached === entry) cached = null;
    });
    cached = entry;
  }
  return cached.access;
}

/** true / false once known, null while the check is in flight. */
export function useStatsAccess(): boolean | null {
  const { user, isAuthenticated, isSuperAdmin } = useAuth();
  const userId = user?.id ?? '';
  const known = isSuperAdmin ? true : cached?.userId === userId ? cached.value ?? null : null;
  const [access, setAccess] = useState<boolean | null>(known);

  useEffect(() => {
    if (!isAuthenticated || !userId) return;
    if (isSuperAdmin) {
      setAccess(true);
      return;
    }
    let live = true;
    statsAccess(userId)
      .then((ok) => {
        if (live) setAccess(ok);
      })
      .catch(() => {
        // Hide Statistics rather than open a page that would 403.
        if (live) setAccess(false);
      });
    return () => {
      live = false;
    };
  }, [isAuthenticated, isSuperAdmin, userId]);

  return access;
}
